import { useId } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import { BOOKING_CITIES } from '../../core/models/booking-cities';

interface CityPickerProps {
    selected: string | null;
    error?: TranslationKey;
    onChange: (city: string | null) => void;
}

/**
 * Which office. Asked only for a meeting in one — an online request names no city, and the server
 * refuses one that carries a city, so hiding this is not cosmetic.
 *
 * It sits on the first step, beside the format, because the next step offers only the consultants
 * who work in the chosen office: a customer in Astana has no use for a list of Almaty's.
 */
export function CityPicker({ selected, error, onChange }: CityPickerProps) {
    const { t, language } = useTranslation();
    const selectId = useId();
    const errorId = useId();

    return (
        <div className="city-field" data-reveal="city">
            <label className="fl" htmlFor={selectId}>
                <span>{t('booking.step.city')}</span> <span className="req">*</span>
            </label>
            <div className="sw">
                <select
                    id={selectId}
                    className={error === undefined ? undefined : 'err'}
                    value={selected ?? ''}
                    required
                    aria-invalid={error !== undefined}
                    aria-describedby={error === undefined ? undefined : errorId}
                    onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
                >
                    <option value="">{t('booking.city.placeholder')}</option>
                    {BOOKING_CITIES.map((city) => (
                        <option key={city.key} value={city.key}>
                            {language === 'ru' ? city.nameRu : city.nameKk}
                        </option>
                    ))}
                </select>
            </div>

            {error !== undefined && (
                <p className="fe" id={errorId} role="alert">
                    {t(error)}
                </p>
            )}
        </div>
    );
}
